import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Button,
  Chip,
  Paper,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';

/**
 * FillBlankInput - Component for managing multiple blanks in fill-blank questions
 * 
 * @param {Array} blanks - Array of blank objects with position and correctAnswers
 * @param {Function} onChange - Callback when blanks change
 * @param {string} questionText - The question text to show blank positions
 */
const FillBlankInput = ({ blanks = [], onChange, questionText = '' }) => {
  const [localBlanks, setLocalBlanks] = useState(blanks);
  const isInitialMount = React.useRef(true);
  const prevBlanksRef = React.useRef(blanks);

  // Update localBlanks when blanks prop changes from parent
  useEffect(() => {
    const blanksChanged = JSON.stringify(blanks) !== JSON.stringify(prevBlanksRef.current);
    if (blanksChanged) {
      prevBlanksRef.current = blanks;
      setLocalBlanks(blanks);
    }
  }, [blanks]);

  // Notify parent when localBlanks changes (but not on initial mount)
  useEffect(() => {
    // Skip calling onChange on initial mount to prevent infinite loop
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Only call onChange if localBlanks differs from the prop
    const blanksChanged = JSON.stringify(blanks) !== JSON.stringify(localBlanks);
    if (onChange && blanksChanged) {
      onChange(localBlanks);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localBlanks]);

  const handleAddBlank = () => {
    const newPosition = localBlanks.length > 0 
      ? Math.max(...localBlanks.map(b => b.position)) + 1 
      : 1;
    
    setLocalBlanks([
      ...localBlanks,
      { position: newPosition, correctAnswers: [''], firstCharacterHint: false }
    ]);
  };

  const handleRemoveBlank = (index) => {
    setLocalBlanks(localBlanks.filter((_, i) => i !== index));
  };

  const handlePositionChange = (index, newPosition) => {
    const updatedBlanks = [...localBlanks];
    updatedBlanks[index] = {
      ...updatedBlanks[index],
      position: parseInt(newPosition) || 1
    };
    setLocalBlanks(updatedBlanks);
  };

  const handleAddAnswer = (blankIndex) => {
    const updatedBlanks = localBlanks.map((blank, i) =>
      i === blankIndex
        ? { ...blank, correctAnswers: [...blank.correctAnswers, ''] }
        : blank
    );
    setLocalBlanks(updatedBlanks);
  };

  const handleRemoveAnswer = (blankIndex, answerIndex) => {
    const updatedBlanks = localBlanks.map((blank, i) =>
      i === blankIndex
        ? { ...blank, correctAnswers: blank.correctAnswers.filter((_, j) => j !== answerIndex) }
        : blank
    );
    setLocalBlanks(updatedBlanks);
  };

  const handleAnswerChange = (blankIndex, answerIndex, value) => {
    const updatedBlanks = localBlanks.map((blank, i) =>
      i === blankIndex
        ? {
            ...blank,
            correctAnswers: blank.correctAnswers.map((a, j) => (j === answerIndex ? value : a)),
          }
        : blank
    );
    setLocalBlanks(updatedBlanks);
  };

  const handleFirstCharacterHintChange = (blankIndex, checked) => {
    const updatedBlanks = localBlanks.map((blank, i) =>
      i === blankIndex ? { ...blank, firstCharacterHint: checked } : blank
    );
    setLocalBlanks(updatedBlanks);
  };

  const renderQuestionPreview = () => {
    if (!questionText) return null;

    let preview = questionText;
    const sortedBlanks = [...localBlanks].sort((a, b) => a.position - b.position);
    
    // Replace all blank placeholders with styled blanks
    sortedBlanks.forEach(blank => {
      const placeholder = `{{${blank.position}}}`;
      // Use a global replace to handle multiple occurrences
      const regex = new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g');
      preview = preview.replace(
        regex,
        `<span style="background-color: #e3f2fd; padding: 2px 8px; border-radius: 4px; font-weight: bold; color: #1976d2;">___${blank.position}___</span>`
      );
    });

    return (
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          mb: 3, 
          backgroundColor: '#f5f5f5',
          border: '1px solid #e0e0e0'
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Content Preview with Blanks:
        </Typography>
        <Box 
          sx={{ 
            '& p': { margin: '0.5em 0' },
            '& ul, & ol': { marginLeft: '1.5em' }
          }}
          dangerouslySetInnerHTML={{ __html: preview }}
        />
      </Paper>
    );
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Fill-in-the-Blank Configuration
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddBlank}
          size="small"
        >
          Add Blank
        </Button>
      </Box>

      <Box mb={3}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          💡 <strong>How to use:</strong> In the main content above, use <code>{'{{1}}'}</code>, <code>{'{{2}}'}</code>, etc. to mark blank positions.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Example: "The weather is <code>{'{{1}}'}</code> today, so we should <code>{'{{2}}'}</code> an umbrella."
        </Typography>
      </Box>

      {renderQuestionPreview()}

      {localBlanks.length === 0 ? (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            textAlign: 'center', 
            backgroundColor: '#fafafa',
            border: '2px dashed #e0e0e0'
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No blanks added yet. Click "Add Blank" to create your first blank.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {localBlanks.map((blank, blankIndex) => (
            <Grid item xs={12} key={blankIndex}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 2,
                  border: '2px solid #e3f2fd',
                  '&:hover': {
                    borderColor: '#1976d2',
                  }
                }}
              >
                <Box display="flex" alignItems="flex-start" gap={2}>
                  <Box sx={{ mt: 1 }}>
                    <DragIcon color="action" />
                  </Box>
                  
                  <Box flexGrow={1}>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <TextField
                        label="Position"
                        type="number"
                        value={blank.position}
                        onChange={(e) => handlePositionChange(blankIndex, e.target.value)}
                        size="small"
                        sx={{ width: 100 }}
                        inputProps={{ min: 1 }}
                        required
                      />
                      <Chip 
                        label={`Blank {{${blank.position}}}`} 
                        color="primary" 
                        size="small"
                      />
                      <Box flexGrow={1} />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveBlank(blankIndex)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>

                    <Box mb={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={blank.firstCharacterHint || false}
                            onChange={(e) => handleFirstCharacterHintChange(blankIndex, e.target.checked)}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Show first character as hint
                          </Typography>
                        }
                      />
                    </Box>

                    <Typography variant="subtitle2" gutterBottom>
                      Correct Answers (synonyms accepted):
                    </Typography>
                    
                    {blank.correctAnswers.map((answer, answerIndex) => (
                      <Box 
                        key={answerIndex} 
                        display="flex" 
                        alignItems="center" 
                        gap={1} 
                        mb={1}
                      >
                        <TextField
                          fullWidth
                          label={`Answer ${answerIndex + 1}`}
                          value={answer}
                          onChange={(e) => handleAnswerChange(blankIndex, answerIndex, e.target.value)}
                          size="small"
                          placeholder="Enter correct answer"
                          required={answerIndex === 0}
                        />
                        {blank.correctAnswers.length > 1 && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveAnswer(blankIndex, answerIndex)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    ))}

                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {localBlanks.length > 0 && (
        <Box mt={2}>
          <Typography variant="caption" color="text.secondary">
            ℹ️ Students can enter any of the correct answers (case-insensitive). All blanks must be correct for the question to be marked as correct.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default FillBlankInput;
